import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { PaginationSettingsTableFooter } from 'wbs-components-lib';

import { Avatar, Box, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridFilterModel, GridRenderCellParams } from '@mui/x-data-grid';

import DefaultAvatarIcon from '@assets/default-avatar.webp';

import { tableStyles } from '@shared/styles';
import { getFilterOperators } from '@shared/utils';

import { useLazyGetUsersQuery } from '@services';

import { AppDispatch, getUsersListSelector, setUsers } from '@store';

import { VipUserTasksConfigurationModal } from '../VipModals/VipUserTasksConfigurationModal';

import { USERS_PAGE_LIMIT } from './constants';
import { UserSettingsTableActionsMenu } from './UserSettingsTableActionsMenu';

import styles from './UsersTable.module.css';

import { UserModel } from 'v2/types/UserModel';

const { tableContainer } = tableStyles;
const { userNameContainer } = styles;

export const UsersTable = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [filterString, setFilterString] = useState<string>('');
  const [isVipTaskStatusUpdateModalOpened, setIsVipTaskStatusUpdateModalOpened] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<UserModel>({} as UserModel);

  const usersList = useSelector(getUsersListSelector);

  const [getUsers] = useLazyGetUsersQuery();

  const getInitialUsers = async () => {
    const { result: initialUsers, total } = await getUsers({
      limit: USERS_PAGE_LIMIT,
      page,
    }).unwrap();

    setTotalCount(total);

    dispatch(setUsers(initialUsers));
  };

  useEffect(() => {
    setIsLoading(true);

    getInitialUsers();

    setIsLoading(false);
  }, []);

  const debouncedHandleFetchUsers = useCallback(async () => {
    setIsLoading(true);

    const currentPage = page + 1;

    const { result: users } = await getUsers({
      limit: USERS_PAGE_LIMIT,
      page: currentPage,
      filter: filterString,
    }).unwrap();

    dispatch(setUsers([...usersList, ...users]));

    setPage(currentPage);

    setIsLoading(false);
  }, [page, usersList, filterString]);

  const filterUsers = useCallback(async () => {
    setIsLoading(true);

    if (filterString !== '') {
      const { result: users, total } = await getUsers({
        limit: USERS_PAGE_LIMIT,
        page: 1,
        filter: filterString,
      }).unwrap();

      setPage(1);
      setTotalCount(total);

      dispatch(setUsers(users));
    } else {
      const { result: users, total } = await getUsers({
        limit: USERS_PAGE_LIMIT,
        page: 1,
        filter: filterString,
      }).unwrap();

      setPage(1);
      setTotalCount(total);

      dispatch(setUsers(users));
    }

    setIsLoading(false);
  }, [filterString]);

  useEffect(() => {
    filterUsers();
  }, [filterString]);

  const onFilterChange = (filterModel: GridFilterModel) => {
    const { value, field } = filterModel.items[0];

    if (!!value) {
      const filterString = `${field},${value}`;

      setFilterString(filterString);
    } else {
      setFilterString('');
    }
  };

  const handleVipTaskStatusUpdateModalOpen = (user: UserModel) => {
    setIsVipTaskStatusUpdateModalOpened(true);

    setCurrentUser(user);
  };

  const handleConfigureUserBalanceModalClose = () => {
    setIsVipTaskStatusUpdateModalOpened(false);

    setCurrentUser({} as UserModel);
  };

  const userColumns: GridColDef[] = [
    {
      field: 'imgUrl',
      headerName: 'Avatar',
      maxWidth: 100,
      filterOperators: getFilterOperators(['contains']),
      flex: 1,
      hideable: false,
      renderCell: (params: GridRenderCellParams<any, UserModel>) => {
        const {
          row: { imgUrl, id },
        } = params;

        return (
          <Box key={id} className={userNameContainer}>
            <Avatar src={imgUrl ?? DefaultAvatarIcon} />
          </Box>
        );
      },
    },
    {
      field: 'telegramUserName',
      headerName: 'Telegram User Name',
      minWidth: 200,
      filterOperators: getFilterOperators(['contains']),
      flex: 1,
      hideable: false,
      renderCell: (params: GridRenderCellParams<any, UserModel>) => {
        const {
          row: { telegramUserName = '-', id },
        } = params;

        return (
          <Typography key={id} overflow="hidden" textOverflow="ellipsis">
            {telegramUserName}
          </Typography>
        );
      },
    },
    {
      field: 'userNickname',
      headerName: 'User Nickname',
      minWidth: 200,
      filterOperators: getFilterOperators(['contains']),
      flex: 1,
      hideable: false,
      renderCell: (params: GridRenderCellParams<any, UserModel>) => {
        const {
          row: { userNickname, id },
        } = params;

        return (
          <Typography key={id} overflow="hidden" textOverflow="ellipsis">
            {userNickname}
          </Typography>
        );
      },
    },
    {
      field: 'email',
      headerName: 'E-mail',
      minWidth: 200,
      filterOperators: getFilterOperators(['contains']),
      flex: 1,
      hideable: false,
      renderCell: (params: GridRenderCellParams<any, UserModel>) => {
        const {
          row: { email = '-', id },
        } = params;

        return (
          <Typography key={id} overflow="hidden" textOverflow="ellipsis">
            {email}
          </Typography>
        );
      },
    },
    {
      field: 'levelVipPoints',
      headerName: 'VIP points amount',
      minWidth: 200,
      flex: 1,
      hideable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      maxWidth: 100,
      flex: 1,
      hideable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<any, UserModel>) => {
        const { row } = params;

        return (
          <UserSettingsTableActionsMenu
            user={row}
            handleVipTaskStatusUpdateModalOpen={handleVipTaskStatusUpdateModalOpen}
          />
        );
      },
    },
  ];

  const isShowMoreDisabled = totalCount === usersList.length;

  const { id: userId, userNickname } = currentUser;

  return (
    <>
      <Box className={tableContainer}>
        <DataGrid
          rows={usersList}
          loading={isLoading}
          rowCount={1}
          filterMode="server"
          getRowHeight={() => 'auto'}
          onFilterModelChange={onFilterChange}
          columns={userColumns}
          hideFooterPagination
          // components={{
          //   LoadingOverlay: () => <BaseSpinner type="linear" />,
          // }}
        />
        <PaginationSettingsTableFooter
          totalCount={totalCount}
          isShowMoreDisabled={isShowMoreDisabled}
          fetch={debouncedHandleFetchUsers as () => Promise<void>}
          elementsCount={usersList.length}
        />
      </Box>
      <VipUserTasksConfigurationModal
        handleClose={handleConfigureUserBalanceModalClose}
        isOpened={isVipTaskStatusUpdateModalOpened}
        userId={userId}
        userNickname={userNickname}
        refetch={getInitialUsers}
      />
    </>
  );
};
